import React, { createContext, useContext, useState, useCallback } from 'react';
import { collectProjectPages } from '../utils/collectProjectPages';
import { View, Role } from '../constants/roles';
import { useMsal } from '@azure/msal-react';
import { impersonationHeaders } from '../utils/impersonationFetch';
import { ApiError, apiErrorFromResponse, toApiError } from '../utils/apiError';
import { gapEvidenceReasonText } from '../utils/gapToolEvidence';
import { omitUnchangedProfileKeys } from '../utils/omitUnchangedProfileKeys';
const AppContext = createContext(null);

/**
 * Technical-evaluation routes carry `requestId` in error.details (not httpRequestId); fold it in so the
 * shared apiError message shows the "Reference: <id>" for 5xx. Returns the failure shape the callers use.
 */
const TECH_EVAL_PLAIN_REASONS = new Set(['STAGE_PREREQUISITE_NOT_MET', 'GAP_ANALYSIS_LOCKED', 'TECH_ASSESSMENT_NOT_COMPLETE', 'STALE_EVALUATION']);

function technicalEvaluationFailure(res, json) {
    const details = json?.error?.details || null;
    const envelope = details && details.requestId && !details.httpRequestId
        ? { ...json, error: { ...json.error, details: { ...details, httpRequestId: details.requestId } } }
        : json;
    const err = apiErrorFromResponse(res, envelope);
    return {
        ok: false,
        status: res.status,
        // 409 refusals (stage not approved, gap analysis locked, assessment not complete) get plain text.
        errorMessage: (res.status === 409 && TECH_EVAL_PLAIN_REASONS.has(details?.reason) && gapEvidenceReasonText(details.reason, details)) || err.message,
        reason: details?.reason || null,
        retryable: details?.retryable === true,
        requestId: details?.requestId || details?.httpRequestId || null,
        failedRowIds: Array.isArray(details?.failedRowIds) ? details.failedRowIds.map(String) : null,
        // 400 OVERRIDE_NOTE_REQUIRED: the rows whose override still lacks a reason.
        overrideNoteRowIds: details?.reason === 'OVERRIDE_NOTE_REQUIRED' && Array.isArray(details?.rowIds) ? details.rowIds.map(String) : null,
    };
}

/**
 * Global application state provider.
 * Manages: user, current view, projects, active project, notifications.
 */
export function AppProvider({ children }) {
    const { instance, accounts } = useMsal();
    const [user, setUser] = useState(null);
    const [currentView, setCurrentViewRaw] = useState(View.LANDING);
    const [projects, setProjects] = useState([]);
    const [projectsLoading, setProjectsLoading] = useState(false);
    const [activeProjectId, setActiveProjectIdRaw] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(true);

    const setCurrentView = useCallback((view) => {
        setCurrentViewRaw(view);
        try { sessionStorage.setItem('pl_currentView', view); } catch {}
    }, []);
    const setActiveProjectId = useCallback((id) => {
        setActiveProjectIdRaw(id);
        try { sessionStorage.setItem('pl_activeProjectId', id || ''); } catch {}
    }, []);
    const [notifications, setNotifications] = useState([]);
    const [taxonomyPendingCount, setTaxonomyPendingCount] = useState(0);

    /** MSAL / portal role from sign-in — restored on exitProject; not cleared when leaving a project */
    const tokenRoleRef = React.useRef(null);
    // QE-02: last server-confirmed raw scopingProfile per project (from GET /profile + our own saves).
    const profileBaselineRef = React.useRef({});
    /** When opening the workbench from Tasks, set 1–3; ProjectWorkspace consumes once */
    const workspaceStepOverrideRef = React.useRef(null);
    /** When opening from Dashboard after AI profiling: Company Onboarding vs profile doc tab */
    const profilingSubTabOverrideRef = React.useRef(null);
    /** Risk Analysis sub-tab (register | treatment | soa) when deep-linking from portfolio */
    const riskAnalysisSubTabOverrideRef = React.useRef(null);
    /** Evidencing & Monitoring sub-tab (incidents | evidence | dashboard) when opening from Tasks */
    const evidencingSubTabOverrideRef = React.useRef(null);
    /** Set after fetchProjectProfile is defined — used to refresh open project after fetchProjects (no stale profile). */
    const fetchProjectProfileRef = React.useRef(null);

    const activeProject = projects.find((p) => p.id === activeProjectId) || null;

    // ── Fetch Projects from Backend ──────────────────────────
    const fetchProjects = useCallback(async () => {
        if (!accounts || accounts.length === 0) return null;
        setProjectsLoading(true);
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                scopes: [process.env.REACT_APP_API_SCOPE],
                account: accounts[0]
            });

            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/projects`, {
                headers: { 'Authorization': `Bearer ${tokenResponse.accessToken}` }
            });

            const json = await response.json();
            if (response.ok && json.success) {
                // Platform-admin responses are paginated (backend caps a page at 200 projects). Follow
                // `pagination` so the shell still holds the full list; non-admin responses carry none.
                const collected = await collectProjectPages(json.data, async (next) => {
                    const pageRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/projects?page=${next}`, {
                        headers: { 'Authorization': `Bearer ${tokenResponse.accessToken}` }
                    });
                    const pageJson = await pageRes.json();
                    if (!pageRes.ok || !pageJson.success) {
                        console.error('Failed to fetch projects page', next, pageJson.error?.message || pageRes.statusText);
                        return null;
                    }
                    return pageJson.data;
                });
                // A partial list must be visible to the user, not only in the console.
                if (collected.warnings.length > 0) {
                    // antd is loaded lazily so unit tests of this context do not pull in the UI library.
                    import('antd')
                        .then(({ message }) => collected.warnings.forEach((w) => message.warning(w, 8)))
                        .catch(() => collected.warnings.forEach((w) => console.warn(w)));
                }
                const allRawProjects = collected.projects;
                const mappedProjects = allRawProjects.map(p => ({
                    ...p,
                    companyName: p.companyName || p.name,
                    profilingStatus: p.profilingStatus || 'Draft',
                    scopingStatus: p.scopingStatus || 'Draft',
                }));
                // GET /projects has no profile blob. Re-attaching the in-memory profile for the **active**
                // project avoids a frame where ProjectWorkspace sees no profile (gates fail → step flicker)
                // until fetchProjectProfile completes.
                setProjects((prev) => {
                    const prevById = new Map(prev.map((p) => [p.id, p]));
                    return mappedProjects.map((p) => {
                        const old = prevById.get(p.id);
                        const preserve =
                            activeProjectId && p.id === activeProjectId && old && old.profile;
                        return preserve ? { ...p, profile: old.profile } : { ...p };
                    });
                });
                try {
                    const aid =
                        typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('pl_activeProjectId') : null;
                    if (aid) {
                        setTimeout(() => fetchProjectProfileRef.current?.(aid), 0);
                    }
                } catch {
                    /* ignore */
                }
                return mappedProjects;
            } else {
                console.error('Failed to fetch projects frontend:', json.error?.message || response.statusText);
            }
        } catch (error) {
            console.error('Failed to fetch projects frontend:', error);
        } finally {
            setProjectsLoading(false);
        }
        return null;
    }, [instance, accounts, activeProjectId]);

    // ── Notifications (fetched from backend) ─────────────────
    const fetchNotifications = useCallback(async () => {
        if (!accounts || accounts.length === 0) return;
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                scopes: [process.env.REACT_APP_API_SCOPE],
                account: accounts[0],
            });
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/notifications`, {
                headers: { 'Authorization': `Bearer ${tokenResponse.accessToken}` },
            });
            const json = await res.json();
            if (res.ok && json.success) {
                setNotifications(json.data.notifications.map(n => ({
                    id: n.id,
                    projectId: n.projectId,
                    type: n.type,
                    title: n.title,
                    text: n.message,
                    read: n.isRead,
                    actorRole: n.actorRole,
                    targetView: n.targetView || 'PROJECT_WORKSPACE',
                    timestamp: new Date(n.createdAt).getTime(),
                })));
            }
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        }
    }, [instance, accounts]);

    const fetchTaxonomyPendingCount = useCallback(async () => {
        if (!accounts || accounts.length === 0) return;
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                scopes: [process.env.REACT_APP_API_SCOPE],
                account: accounts[0],
            });
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/admin/taxonomy-pending-count`, {
                headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
            });
            const json = await res.json();
            if (res.ok && json.success) {
                setTaxonomyPendingCount(typeof json.data?.count === 'number' ? json.data.count : 0);
            }
        } catch {
            /* ignore — non-admin or offline */
        }
    }, [instance, accounts]);

    React.useEffect(() => {
        if (!user || user.role !== Role.ADMIN) {
            setTaxonomyPendingCount(0);
            return;
        }
        fetchTaxonomyPendingCount();
        const t = setInterval(fetchTaxonomyPendingCount, 120000);
        return () => clearInterval(t);
    }, [user, fetchTaxonomyPendingCount]);

    const addNotification = useCallback((text) => {
        setNotifications((prev) => [
            { id: `notif-${Date.now()}`, text, read: false, timestamp: Date.now() },
            ...prev,
        ]);
    }, []);

    const markNotificationRead = useCallback(async (id) => {
        setNotifications((prev) =>
            prev.map((n) => (n.id === id ? { ...n, read: true } : n))
        );
        if (!accounts || accounts.length === 0) return;
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                scopes: [process.env.REACT_APP_API_SCOPE],
                account: accounts[0],
            });
            await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/notifications/${id}/read`, {
                method: 'PATCH',
                headers: { 'Authorization': `Bearer ${tokenResponse.accessToken}` },
            });
        } catch { /* best-effort */ }
    }, [instance, accounts]);

    const markAllNotificationsRead = useCallback(() => {
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    }, []);

    // ── Sign In (called after MSAL authenticates) ────────────
    const signIn = useCallback((userData) => {
        tokenRoleRef.current = userData.role;
        setUser(userData);

        // Restore session state on refresh; fall back to role default
        const savedView = (() => { try { return sessionStorage.getItem('pl_currentView'); } catch { return null; } })();
        const savedProjectId = (() => { try { return sessionStorage.getItem('pl_activeProjectId'); } catch { return null; } })();
        const validViews = new Set(Object.values(View));

        if (savedView && validViews.has(savedView)) {
            setCurrentView(savedView);
            if (savedProjectId) {
                setActiveProjectId(savedProjectId);
            }
        } else if (userData.role === Role.COMPANY_DELEGATE) {
            setCurrentView(View.TASK_CENTER);
        } else if (userData.role === Role.SUPPORT_AGENT) {
            setCurrentView(View.SUPPORT);
        } else {
            setCurrentView(View.DASHBOARD);
        }
        fetchProjects();
        fetchNotifications();
        if (userData.role === Role.ADMIN) {
            fetchTaxonomyPendingCount();
        }
        // Fetch the DB user record to get the real email/ID (MSAL preferred_username
        // for B2B guests is often {oid}@tenant.onmicrosoft.com, not the real email).
        (async () => {
            try {
                const tokenResponse = await instance.acquireTokenSilent({
                    scopes: [process.env.REACT_APP_API_SCOPE],
                    account: accounts[0],
                });
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/me`, {
                    headers: { 'Authorization': `Bearer ${tokenResponse.accessToken}` },
                });
                const json = await res.json();
                if (res.ok && json.success && json.data?.dbEmail) {
                    setUser((prev) => prev ? {
                        ...prev,
                        dbUserId: json.data.dbUserId || prev.id,
                        email: json.data.dbEmail || prev.email,
                        dbEmail: json.data.dbEmail,
                    } : prev);
                }
            } catch (e) {
                console.warn('Failed to fetch DB identity:', e);
            }
        })();
    }, [fetchProjects, fetchNotifications, fetchTaxonomyPendingCount, instance, accounts, setCurrentView, setActiveProjectId]);

    const signOut = useCallback(() => {
        tokenRoleRef.current = null;
        setUser(null);
        setActiveProjectId(null);
        setCurrentView(View.LANDING);
        try { sessionStorage.removeItem('pl_currentView'); sessionStorage.removeItem('pl_activeProjectId'); } catch {}
        instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
    }, [instance, setActiveProjectId, setCurrentView]);

    // ── Fetch Members for a specific project ─────────────────
    const fetchProjectMembers = useCallback(async (projectId) => {
        if (!accounts || accounts.length === 0 || !projectId) return [];
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                scopes: [process.env.REACT_APP_API_SCOPE],
                account: accounts[0]
            });

            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/projects/${projectId}/members`, {
                headers: { 'Authorization': `Bearer ${tokenResponse.accessToken}` }
            });

            const json = await response.json();
            if (response.ok && json.success) {
                return json.data.members || [];
            }
            console.error('Failed to fetch project members:', json.error?.message || response.statusText);
            return [];
        } catch (error) {
            console.error('Failed to fetch project members:', error);
            return [];
        }
    }, [instance, accounts]);

    // ── Fetch project profile from DB ───────────────────────
    /** @returns {Promise<object|null>} Merged profile for the project, or null if fetch failed. */
    const fetchProjectProfile = useCallback(async (projectId) => {
        if (!accounts || accounts.length === 0 || !projectId) return null;
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                scopes: [process.env.REACT_APP_API_SCOPE],
                account: accounts[0]
            });

            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/projects/${projectId}/profile`, {
                headers: { 'Authorization': `Bearer ${tokenResponse.accessToken}` }
            });

            const json = await response.json().catch(() => ({}));
            if (response.ok && json.success) {
                const d = json.data;
                if (d && d.scopingProfile && typeof d.scopingProfile === 'object') {
                    profileBaselineRef.current[projectId] = d.scopingProfile;
                }
                const {
                    profilingStatus,
                    scopingStatus,
                    attributes,
                    scopingProfile,
                    // Task #128 fast-follow: DPDP/GDPR controller-processor-both role, drafted by LA/Advisor,
                    // confirmed by LA/SPOC — filters gap questions per scoping.controller.regulatoryRoleFilter.
                    regulatoryRole,
                    regulatoryRoleConfirmed,
                    regulatoryRoleConfirmedBy,
                    regulatoryRoleConfirmedAt,
                } = d;
                let mergedForProject = null;
                setProjects((prev) =>
                    prev.map((p) => {
                        if (p.id !== projectId) return p;
                        // Server JSON is source of truth — do not spread old p.profile first or removed
                        // keys (e.g. gapQuestionnaireDocument after reset) never disappear on the client.
                        const serverSp = scopingProfile && typeof scopingProfile === 'object' ? scopingProfile : {};
                        const prevProfile = p.profile && typeof p.profile === 'object' ? p.profile : {};
                        const mergedProfile = {
                            ...serverSp,
                            attributes: Array.isArray(attributes) ? attributes : [],
                        };
                        const prevOw = prevProfile.organizationalSetupWizard;
                        if (prevOw && typeof prevOw === 'object') {
                            if (
                                !mergedProfile.organizationalSetupWizard ||
                                typeof mergedProfile.organizationalSetupWizard !== 'object'
                            ) {
                                mergedProfile.organizationalSetupWizard = prevOw;
                            } else {
                                mergedProfile.organizationalSetupWizard = {
                                    ...prevOw,
                                    ...mergedProfile.organizationalSetupWizard,
                                };
                            }
                        }
                        const prevCip = prevProfile.controlImplementationPackage;
                        if (prevCip && typeof prevCip === 'object') {
                            if (
                                !mergedProfile.controlImplementationPackage ||
                                typeof mergedProfile.controlImplementationPackage !== 'object'
                            ) {
                                mergedProfile.controlImplementationPackage = prevCip;
                            } else {
                                // Shallow-merge the package metadata, but deep-merge docs by controlId so
                                // optimistic updates persist while server-driven stage advances win.
                                const prevDocs =
                                    prevCip.docs && typeof prevCip.docs === 'object' ? prevCip.docs : {};
                                const serverDocs =
                                    mergedProfile.controlImplementationPackage.docs &&
                                    typeof mergedProfile.controlImplementationPackage.docs === 'object'
                                        ? mergedProfile.controlImplementationPackage.docs
                                        : {};
                                mergedProfile.controlImplementationPackage = {
                                    ...prevCip,
                                    ...mergedProfile.controlImplementationPackage,
                                    docs: { ...prevDocs, ...serverDocs },
                                };
                            }
                        }
                        const prevImp = prevProfile.incidentMonitoringPackage;
                        if (prevImp && typeof prevImp === 'object') {
                            if (
                                !mergedProfile.incidentMonitoringPackage ||
                                typeof mergedProfile.incidentMonitoringPackage !== 'object'
                            ) {
                                mergedProfile.incidentMonitoringPackage = prevImp;
                            } else {
                                mergedProfile.incidentMonitoringPackage = {
                                    ...prevImp,
                                    ...mergedProfile.incidentMonitoringPackage,
                                };
                            }
                        }
                        if (Object.prototype.hasOwnProperty.call(d, 'onboardingAssetManifest')) {
                            mergedProfile.onboardingAssetManifest = d.onboardingAssetManifest;
                        }
                        mergedForProject = mergedProfile;
                        return {
                            ...p,
                            profilingStatus: profilingStatus || p.profilingStatus || 'Draft',
                            scopingStatus: scopingStatus || p.scopingStatus || 'Draft',
                            regulatoryRole: regulatoryRole ?? p.regulatoryRole ?? null,
                            regulatoryRoleConfirmed: regulatoryRoleConfirmed ?? p.regulatoryRoleConfirmed ?? false,
                            regulatoryRoleConfirmedBy: regulatoryRoleConfirmedBy ?? p.regulatoryRoleConfirmedBy ?? null,
                            regulatoryRoleConfirmedAt: regulatoryRoleConfirmedAt ?? p.regulatoryRoleConfirmedAt ?? null,
                            // Read-only server disclosure, kept on the project (never inside `profile`) so no PUT can echo it back.
                            legacyCompletedWithoutEvidence: d.legacyCompletedWithoutEvidence === true,
                            techEvaluationHistoryTampered: d.techEvaluationHistoryTampered === true,
                            profile: mergedProfile,
                        };
                    })
                );
                return mergedForProject;
            }
        } catch (error) {
            console.error('Failed to fetch project profile:', error);
        }
        return null;
    }, [instance, accounts]);

    React.useEffect(() => {
        fetchProjectProfileRef.current = fetchProjectProfile;
    }, [fetchProjectProfile]);

    // ── Project selection (portal role unchanged — one persona per email) ──
    const selectProject = useCallback(
        (project, options = {}) => {
            if (!user) return;

            const step = options?.initialWorkspaceStep;
            workspaceStepOverrideRef.current =
                typeof step === 'number' && step >= 1 && step <= 9 ? step : null;

            const subTab = options?.initialProfilingSubTab;
            profilingSubTabOverrideRef.current =
                subTab === 'onboarding' || subTab === 'profile' ? subTab : null;

            const riskTab = options?.initialRiskAnalysisSubTab;
            riskAnalysisSubTabOverrideRef.current =
                riskTab === 'register' || riskTab === 'treatment' || riskTab === 'soa' ? riskTab : null;

            const evTab = options?.initialEvidencingSubTab;
            evidencingSubTabOverrideRef.current =
                evTab === 'incidents' || evTab === 'evidence' || evTab === 'dashboard' ? evTab : null;

            const isMember = project.members?.some((m) => m.id === user.id);
            const isLead = project.leadAdvisor === user.name;

            const accessRole = tokenRoleRef.current || user.role;

            if (
                accessRole !== Role.ADMIN &&
                accessRole !== Role.LEAD_ADVISOR &&
                accessRole !== Role.ADVISOR &&
                accessRole !== Role.COMPANY_SPOC &&
                accessRole !== Role.COMPANY_DELEGATE &&
                !isLead &&
                !isMember
            ) {
                console.warn(`Unauthorized Access: You are not a member of project ${project.name}.`);
                return;
            }

            setActiveProjectId(project.id);
            fetchProjectProfile(project.id);

            if (accessRole === Role.ADMIN && !options.forceWorkbench) {
                setCurrentView(View.PROJECT_MEMBERS);
            } else {
                setCurrentView(View.PROJECT_WORKSPACE);
            }
        },
        [user, fetchProjectProfile, setActiveProjectId, setCurrentView]
    );

    const exitProject = useCallback(() => {
        if (user?.role === Role.COMPANY_DELEGATE) return;
        setActiveProjectId(null);
        const portalRole = tokenRoleRef.current;
        if (portalRole) {
            setUser((prev) => (prev ? { ...prev, role: portalRole } : prev));
        }
        setCurrentView(View.DASHBOARD);
    }, [user, setActiveProjectId, setCurrentView]);

    // ── Projects CRUD ────────────────────────────────────────
    const addProject = useCallback((project) => {
        setProjects((prev) => [project, ...prev]);
    }, []);

    const toggleProjectStatus = useCallback((projectId) => {
        setProjects((prev) =>
            prev.map((p) =>
                p.id === projectId ? { ...p, isDisabled: !p.isDisabled } : p
            )
        );
    }, []);

    const deleteProject = useCallback(async (projectId) => {
        if (!accounts || accounts.length === 0 || !projectId) return false;
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                scopes: [process.env.REACT_APP_API_SCOPE],
                account: accounts[0],
            });
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/projects/${projectId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${tokenResponse.accessToken}` },
            });
            const json = await res.json().catch(() => ({}));
            if (res.ok && json.success) {
                setProjects((prev) => prev.filter((p) => p.id !== projectId));
                if (activeProjectId === projectId) {
                    setActiveProjectId(null);
                    setCurrentView(View.DASHBOARD);
                }
                return { ok: true };
            }
            const msg = json.error?.message || res.statusText || 'Request failed';
            console.error('Failed to delete project:', msg);
            return { ok: false, errorMessage: msg };
        } catch (error) {
            console.error('Failed to delete project:', error);
            return { ok: false, errorMessage: error?.message || 'Network error' };
        }
    }, [instance, accounts, activeProjectId]);

    const updateProjectMembers = useCallback(
        (members) => {
            if (!activeProjectId) return;
            setProjects((prev) =>
                prev.map((p) =>
                    p.id === activeProjectId ? { ...p, members } : p
                )
            );
        },
        [activeProjectId]
    );

    const updateProjectProfile = useCallback(
        (projectId, profile) => {
            setProjects((prev) =>
                prev.map((p) => {
                    if (p.id !== projectId) return p;
                    const prevProf = p.profile && typeof p.profile === 'object' ? p.profile : {};
                    const nextProf = profile && typeof profile === 'object' ? { ...profile } : profile;
                    if (nextProf && typeof nextProf === 'object') {
                        // Owned by setActiveWorkspaceStep — never let an optimistic profile patch (which
                        // typically spreads a stale `...project.profile` as base) clobber the latest
                        // navigation step. Otherwise after every save/refetch round-trip the user gets
                        // bounced back to a previously-saved step (e.g. Organizational Setup).
                        if (typeof prevProf.workspaceActiveStep === 'number') {
                            nextProf.workspaceActiveStep = prevProf.workspaceActiveStep;
                        } else {
                            delete nextProf.workspaceActiveStep;
                        }
                        const prevOw = prevProf.organizationalSetupWizard;
                        if (prevOw && typeof prevOw === 'object') {
                            if (
                                !nextProf.organizationalSetupWizard ||
                                typeof nextProf.organizationalSetupWizard !== 'object'
                            ) {
                                nextProf.organizationalSetupWizard = prevOw;
                            } else {
                                nextProf.organizationalSetupWizard = {
                                    ...prevOw,
                                    ...nextProf.organizationalSetupWizard,
                                };
                            }
                        }
                        const prevCip = prevProf.controlImplementationPackage;
                        if (prevCip && typeof prevCip === 'object') {
                            if (
                                !nextProf.controlImplementationPackage ||
                                typeof nextProf.controlImplementationPackage !== 'object'
                            ) {
                                nextProf.controlImplementationPackage = prevCip;
                            } else {
                                const prevDocs =
                                    prevCip.docs && typeof prevCip.docs === 'object' ? prevCip.docs : {};
                                const nextDocs =
                                    nextProf.controlImplementationPackage.docs &&
                                    typeof nextProf.controlImplementationPackage.docs === 'object'
                                        ? nextProf.controlImplementationPackage.docs
                                        : {};
                                nextProf.controlImplementationPackage = {
                                    ...prevCip,
                                    ...nextProf.controlImplementationPackage,
                                    docs: { ...prevDocs, ...nextDocs },
                                };
                            }
                        }
                        const prevImp = prevProf.incidentMonitoringPackage;
                        if (prevImp && typeof prevImp === 'object') {
                            if (
                                !nextProf.incidentMonitoringPackage ||
                                typeof nextProf.incidentMonitoringPackage !== 'object'
                            ) {
                                nextProf.incidentMonitoringPackage = prevImp;
                            } else {
                                nextProf.incidentMonitoringPackage = {
                                    ...prevImp,
                                    ...nextProf.incidentMonitoringPackage,
                                };
                            }
                        }
                    }
                    return { ...p, profile: nextProf };
                })
            );
        },
        []
    );

    /**
     * Optimistic, race-safe update of just the workbench step. Applied via a functional setProjects
     * updater so it merges against the latest profile in store (never a stale closure snapshot).
     */
    const setActiveWorkspaceStep = useCallback((projectId, step) => {
        if (!projectId || typeof step !== 'number') return;
        setProjects((prev) =>
            prev.map((p) => {
                if (p.id !== projectId) return p;
                const prevProf = p.profile && typeof p.profile === 'object' ? p.profile : {};
                return { ...p, profile: { ...prevProf, workspaceActiveStep: step } };
            }),
        );
    }, []);

    const saveProjectProfile = useCallback(async (projectId, attributes) => {
        if (!accounts || accounts.length === 0 || !projectId) return;
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                scopes: [process.env.REACT_APP_API_SCOPE],
                account: accounts[0]
            });
            await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/projects/${projectId}/profile`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${tokenResponse.accessToken}`,
                },
                body: JSON.stringify({ attributes }),
            });
        } catch (error) {
            console.error('Failed to save project profile:', error);
        }
    }, [instance, accounts]);

    /** Persists scoping wizard state (RACI, resources, wizard flags, contract draft) to SQL via Project.scoping_profile_json */
    /**
     * @param {string} projectId
     * @param {object} scopingProfile
     * @param {{ impersonated?: boolean, onError?: (err: ApiError) => void }} [opts] — `onError` receives a refused or
     *   failed save as an ApiError (status, code, details.reason) so the caller can say WHY; the return stays `false`.
     *   `reopenReason` (Gap Analysis reopen only, trimmed, max 1000 chars) is sent as a top-level PUT body field.
     *   `impersonated: true` spreads
     *   `impersonationHeaders()` (the `X-Impersonation-Id` header, backend contract:
     *   `policylens-backend/src/middleware/impersonation.ts`) into the request so an active
     *   LA impersonation session's effective role is honoured server-side (R026/R027 "Act on
     *   behalf of {persona}" — .claude/design/approval-matrix-clarification.md). Opt-in and
     *   defaults to `false` so every pre-existing caller (GapAnalysis, ProjectOnboarding,
     *   ControlImplementation, RiskAnalysis, OrganizationalSetup's non-SPOC saves) is
     *   byte-identical — omitting the 3rd argument entirely is the common case and behaves
     *   exactly as before this parameter existed.
     */
    const saveScopingProfile = useCallback(async (projectId, fullScopingProfile, { impersonated = false, onError, reopenReason, sendBackReason } = {}) => {
        if (!accounts || accounts.length === 0 || !projectId) return false;
        // QE-02: send only the top-level keys this caller actually changed vs the last server-confirmed profile.
        const scopingProfile = omitUnchangedProfileKeys(fullScopingProfile, profileBaselineRef.current[projectId]);
        const hasReason = (typeof reopenReason === 'string' && reopenReason) || (typeof sendBackReason === 'string' && sendBackReason);
        if (scopingProfile && typeof scopingProfile === 'object' && Object.keys(scopingProfile).length === 0 && !hasReason) {
            return true; // nothing differs from what the server already holds
        }
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                scopes: [process.env.REACT_APP_API_SCOPE],
                account: accounts[0],
            });
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/projects/${projectId}/profile`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${tokenResponse.accessToken}`,
                    ...(impersonated ? impersonationHeaders() : {}),
                },
                // `reopenReason` is a TOP-LEVEL sibling of scopingProfile (never inside gapAnalysisWizard) and is sent
                // only on the one save that reopens the Gap Analysis assessment; it is never part of the stored profile.
                // `sendBackReason` likewise: only on the SPOC's Under Review -> Draft send-back.
                body: JSON.stringify({ scopingProfile, ...(typeof reopenReason === 'string' && reopenReason ? { reopenReason } : {}), ...(typeof sendBackReason === 'string' && sendBackReason ? { sendBackReason } : {}) }),
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok || !json.success) {
                console.error('Failed to save scoping profile:', json.error?.message || res.statusText);
                // Callers that must tell the user WHY (409 stage/lock refusals on Gap Analysis evidence writes) pass onError.
                if (typeof onError === 'function') onError(apiErrorFromResponse(res, json));
                return false;
            }
            const notifyWarnings = Array.isArray(json.data?.notificationWarnings) ? json.data.notificationWarnings : [];
            if (notifyWarnings.length > 0) {
                import('antd')
                    .then(({ message }) => notifyWarnings.forEach((w) => message.warning(w, 8)))
                    .catch(() => notifyWarnings.forEach((w) => console.warn(w)));
            }
            if (scopingProfile && typeof scopingProfile === 'object') {
                profileBaselineRef.current[projectId] = { ...(profileBaselineRef.current[projectId] || {}), ...scopingProfile };
            }
            fetchNotifications();
            return true;
        } catch (error) {
            console.error('Failed to save scoping profile:', error);
            if (typeof onError === 'function') onError(toApiError(error));
            return false;
        }
    }, [instance, accounts, fetchNotifications]);

    // Both status-change calls resolve `true` only when the server accepted the transition and
    // THROW an ApiError otherwise (403 role refusal, 409, network...), so callers can never
    // mistake a refusal for success. Shown to users via userMessageForError.
    const patchProjectStatus = useCallback(async (projectId, kind, status, comment) => {
        if (!accounts || accounts.length === 0 || !projectId) {
            throw new ApiError('Your sign-in could not be refreshed. Please sign in again.', 0);
        }
        let res;
        let json;
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                scopes: [process.env.REACT_APP_API_SCOPE],
                account: accounts[0]
            });
            res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/projects/${projectId}/${kind}-status`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${tokenResponse.accessToken}`,
                },
                body: JSON.stringify({ status, comment }),
            });
            json = await res.json().catch(() => ({}));
        } catch (error) {
            throw toApiError(error);
        }
        if (!(res.ok && json.success)) throw apiErrorFromResponse(res, json);
        const field = kind === 'profiling' ? 'profilingStatus' : 'scopingStatus';
        setProjects((prev) => prev.map((p) => p.id === projectId ? { ...p, [field]: status } : p));
        fetchNotifications();
        return true;
    }, [instance, accounts, fetchNotifications]);

    const updateProfilingStatusAPI = useCallback(
        (projectId, status, comment = '') => patchProjectStatus(projectId, 'profiling', status, comment),
        [patchProjectStatus]);

    const updateScopingStatusAPI = useCallback(
        (projectId, status, comment = '') => patchProjectStatus(projectId, 'scoping', status, comment),
        [patchProjectStatus]);

    /**
     * Task #128 fast-follow: draft-write the project's DPDP/GDPR regulatory role
     * (controller | processor | both). LA/Advisor (or platform_admin) only — enforced
     * server-side; UI gating in ProjectOnboarding.jsx just avoids a dead affordance.
     * Setting a new draft always un-confirms server-side, so we trust the response shape
     * rather than assuming regulatoryRoleConfirmed stays true.
     */
    const updateRegulatoryRoleAPI = useCallback(async (projectId, regulatoryRole) => {
        if (!accounts || accounts.length === 0 || !projectId) return false;
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                scopes: [process.env.REACT_APP_API_SCOPE],
                account: accounts[0]
            });
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/projects/${projectId}/regulatory-role`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${tokenResponse.accessToken}`,
                },
                body: JSON.stringify({ regulatoryRole }),
            });
            const json = await res.json().catch(() => ({}));
            if (res.ok && json.success) {
                const d = json.data || {};
                setProjects((prev) =>
                    prev.map((p) => p.id === projectId ? {
                        ...p,
                        regulatoryRole: d.regulatoryRole ?? regulatoryRole,
                        regulatoryRoleConfirmed: d.regulatoryRoleConfirmed ?? false,
                        regulatoryRoleConfirmedBy: d.regulatoryRoleConfirmedBy ?? null,
                        regulatoryRoleConfirmedAt: d.regulatoryRoleConfirmedAt ?? null,
                    } : p)
                );
                fetchNotifications();
                return true;
            }
            console.error('Failed to save regulatory role:', json.error?.message || res.statusText);
            return false;
        } catch (error) {
            console.error('Failed to save regulatory role:', error);
            return false;
        }
    }, [instance, accounts, fetchNotifications]);

    /**
     * Task #128 fast-follow: confirm the currently-drafted regulatory role. LA/SPOC (or
     * platform_admin) only — enforced server-side; backend 400s if nothing is drafted yet.
     *
     * Bug fix (sim-spoc finding, see .claude/lessons/audit-log-unconditional-success-
     * before-persisted-check.md for the backend half of this same root cause): HTTP 200
     * here does NOT mean the confirmation persisted — pre-migration environments return
     * `regulatoryRoleConfirmed: false` as an honest no-op signal. Callers must branch on
     * that flag, not on `res.ok`, so we surface it explicitly rather than collapsing the
     * result to a bare boolean.
     */
    const confirmRegulatoryRoleAPI = useCallback(async (projectId) => {
        if (!accounts || accounts.length === 0 || !projectId) return { ok: false };
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                scopes: [process.env.REACT_APP_API_SCOPE],
                account: accounts[0]
            });
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/projects/${projectId}/regulatory-role/confirm`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${tokenResponse.accessToken}`,
                },
            });
            const json = await res.json().catch(() => ({}));
            if (res.ok && json.success) {
                const d = json.data || {};
                const regulatoryRoleConfirmed = d.regulatoryRoleConfirmed === true;
                setProjects((prev) =>
                    prev.map((p) => p.id === projectId ? {
                        ...p,
                        regulatoryRole: d.regulatoryRole ?? p.regulatoryRole,
                        regulatoryRoleConfirmed,
                        regulatoryRoleConfirmedBy: d.regulatoryRoleConfirmedBy ?? null,
                        regulatoryRoleConfirmedAt: d.regulatoryRoleConfirmedAt ?? null,
                    } : p)
                );
                fetchNotifications();
                return { ok: true, regulatoryRoleConfirmed };
            }
            console.error('Failed to confirm regulatory role:', json.error?.message || res.statusText);
            return { ok: false };
        } catch (error) {
            console.error('Failed to confirm regulatory role:', error);
            return { ok: false };
        }
    }, [instance, accounts, fetchNotifications]);

    // ── Milestone Map API helpers ────────────────────────────
    const fetchMilestones = useCallback(async (projectId) => {
        if (!accounts || accounts.length === 0 || !projectId) return [];
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                scopes: [process.env.REACT_APP_API_SCOPE],
                account: accounts[0],
            });
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/projects/${projectId}/milestones`, {
                headers: { 'Authorization': `Bearer ${tokenResponse.accessToken}` },
            });
            const json = await res.json();
            if (res.ok && json.success) {
                return json.data.milestones || [];
            }
            console.error('Failed to fetch milestones:', json.error?.message);
            return [];
        } catch (error) {
            console.error('Failed to fetch milestones:', error);
            return [];
        }
    }, [instance, accounts]);

    const saveMilestones = useCallback(async (projectId, milestones) => {
        if (!accounts || accounts.length === 0 || !projectId) return false;
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                scopes: [process.env.REACT_APP_API_SCOPE],
                account: accounts[0],
            });
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/projects/${projectId}/milestones`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${tokenResponse.accessToken}`,
                },
                body: JSON.stringify({ milestones }),
            });
            const json = await res.json();
            return res.ok && json.success;
        } catch (error) {
            console.error('Failed to save milestones:', error);
            return false;
        }
    }, [instance, accounts]);

    const generateMilestones = useCallback(async (projectId) => {
        if (!accounts || accounts.length === 0 || !projectId) return null;
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                scopes: [process.env.REACT_APP_API_SCOPE],
                account: accounts[0],
            });
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/projects/${projectId}/milestones/generate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${tokenResponse.accessToken}`,
                },
            });
            const json = await res.json();
            if (res.ok && json.success) {
                return json.data.milestones || [];
            }
            console.error('Failed to generate milestones:', json.error?.message);
            return null;
        } catch (error) {
            console.error('Failed to generate milestones:', error);
            return null;
        }
    }, [instance, accounts]);

    /** Latest ISO 27001 Policy Hub controls (Annex A registry) for heatmaps / dashboards. */
    const fetchIso27001Controls = useCallback(async () => {
        if (!accounts || accounts.length === 0) return { controls: [], versionLabel: null };
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                scopes: [process.env.REACT_APP_API_SCOPE],
                account: accounts[0],
            });
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/iso27001/controls`, {
                headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
            });
            const json = await res.json();
            if (res.ok && json.success) {
                return {
                    controls: json.data?.controls || [],
                    versionId: json.data?.versionId ?? null,
                    versionLabel: json.data?.versionLabel ?? null,
                };
            }
            console.error('Failed to fetch ISO 27001 controls:', json.error?.message);
            return { controls: [], versionLabel: null };
        } catch (error) {
            console.error('Failed to fetch ISO 27001 controls:', error);
            return { controls: [], versionLabel: null };
        }
    }, [instance, accounts]);

    const updateProfilingStatus = useCallback(
        (status) => {
            if (!activeProjectId) return;
            setProjects((prev) =>
                prev.map((p) => {
                    if (p.id !== activeProjectId) return p;
                    return { ...p, profilingStatus: status };
                })
            );
        },
        [activeProjectId]
    );

    /**
     * Multi-user sync: React state only updates from this browser unless we refetch.
     * When another role (e.g. SPOC) changes scoping/profiling/contract on the server,
     * Lead Advisors otherwise see stale UI until a full page reload.
     * — Poll while a project is open; refetch when the tab becomes visible or the window gains focus (throttled).
     */
    const lastMultiUserSyncRef = React.useRef(0);
    const syncActiveProjectThrottled = useCallback(() => {
        if (!activeProjectId || !accounts?.length) return;
        const now = Date.now();
        if (now - lastMultiUserSyncRef.current < 2500) return;
        lastMultiUserSyncRef.current = now;
        fetchProjectProfile(activeProjectId);
        fetchNotifications();
    }, [activeProjectId, accounts, fetchProjectProfile, fetchNotifications]);

    React.useEffect(() => {
        if (!activeProjectId || !accounts?.length) return;
        const id = setInterval(() => {
            fetchProjectProfile(activeProjectId);
            fetchNotifications();
        }, 45000);
        return () => clearInterval(id);
    }, [activeProjectId, accounts, fetchProjectProfile, fetchNotifications]);

    React.useEffect(() => {
        if (!activeProjectId || !accounts?.length) return;
        const onVisibility = () => {
            if (document.visibilityState === 'visible') syncActiveProjectThrottled();
        };
        const onFocus = () => syncActiveProjectThrottled();
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('focus', onFocus);
        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('focus', onFocus);
        };
    }, [activeProjectId, accounts, syncActiveProjectThrottled]);

    // On session restore: fetch project profile once activeProjectId is set and accounts are ready
    const sessionProfileFetchedRef = React.useRef(false);
    React.useEffect(() => {
        if (sessionProfileFetchedRef.current || !activeProjectId || !accounts?.length) return;
        sessionProfileFetchedRef.current = true;
        fetchProjectProfile(activeProjectId);
    }, [activeProjectId, accounts, fetchProjectProfile]);

    /** Dashboard project cards: refresh statuses without requiring a manual reload. */
    React.useEffect(() => {
        if (!user || currentView !== View.DASHBOARD) return;
        const id = setInterval(() => fetchProjects(), 90000);
        return () => clearInterval(id);
    }, [user, currentView, fetchProjects]);

    // ── Meetings ──────────────────────────────────────────────
    const createMeeting = useCallback(async (projectId, meetingData) => {
        if (!accounts || accounts.length === 0 || !projectId) return null;
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                scopes: [process.env.REACT_APP_API_SCOPE],
                account: accounts[0],
            });
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/projects/${projectId}/meetings`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${tokenResponse.accessToken}`,
                },
                body: JSON.stringify(meetingData),
            });
            const json = await res.json();
            if (res.ok && json.success) return json.data.meeting;
            console.error('Failed to create meeting:', json.error?.message);
            return null;
        } catch (error) {
            console.error('Failed to create meeting:', error);
            return null;
        }
    }, [instance, accounts]);

    const updateMeeting = useCallback(async (meetingId, data) => {
        if (!accounts || accounts.length === 0 || !meetingId) return null;
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                scopes: [process.env.REACT_APP_API_SCOPE],
                account: accounts[0],
            });
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/meetings/${meetingId}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${tokenResponse.accessToken}`,
                },
                body: JSON.stringify(data),
            });
            const json = await res.json();
            if (res.ok && json.success) return json.data.meeting;
            console.error('Failed to update meeting:', json.error?.message);
            return null;
        } catch (error) {
            console.error('Failed to update meeting:', error);
            return null;
        }
    }, [instance, accounts]);

    const updateMeetingAttendees = useCallback(async (meetingId, attendees) => {
        if (!accounts || accounts.length === 0 || !meetingId) return null;
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                scopes: [process.env.REACT_APP_API_SCOPE],
                account: accounts[0],
            });
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/meetings/${meetingId}/attendees`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${tokenResponse.accessToken}`,
                },
                body: JSON.stringify({ attendees }),
            });
            const json = await res.json();
            if (res.ok && json.success) return json.data.meeting;
            console.error('Failed to update meeting attendees:', json.error?.message);
            return null;
        } catch (error) {
            console.error('Failed to update meeting attendees:', error);
            return null;
        }
    }, [instance, accounts]);

    const generateAiScores = useCallback(async (projectId, payload) => {
        if (!accounts || accounts.length === 0 || !projectId) return null;
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                scopes: [process.env.REACT_APP_API_SCOPE],
                account: accounts[0],
            });
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/projects/${projectId}/gap-analysis/ai-scores`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${tokenResponse.accessToken}`,
                },
                body: JSON.stringify(payload),
            });
            const json = await res.json();
            if (res.ok && json.success) return json.data;
            console.error('Failed to generate AI scores:', json.error?.message);
            return null;
        } catch (error) {
            console.error('Failed to generate AI scores:', error);
            return null;
        }
    }, [instance, accounts]);

    /**
     * Backlog Sheet-1 row 19 — "Run Technical AI Evaluation". Triggers the real
     * `invokeAI()`-backed scoring of the Advisor's approved tool-test summaries
     * (`.claude/lessons/completion-flag-implies-ai-ran-with-no-real-call.md`). The backend
     * persists `gapAnalysisWizard.techObservations`/`techAiEvalRun` directly — the caller
     * should `fetchProjectProfile(projectId)` after a truthy return to pick up the fresh
     * wizard state, same as every other dedicated-route write in this file.
     * @param {{ impersonated?: boolean, retryFailedOnly?: boolean }} [opts] — `impersonated` is the same
     *   LA-override contract as `saveScopingProfile`'s third argument (`.claude/design/impersonation.md`);
     *   `retryFailedOnly` re-scores only the rows whose AI call failed.
     * 200 data carries techAiEvalFailedRows / techAiEvalPartial / techObservations. A 502/503 means
     * NOTHING was saved: the failure carries `retryable` and `requestId`.
     * @returns {Promise<{ ok: true, data: object } | { ok: false, status: number, errorMessage: string, retryable: boolean, requestId: string|null }>}
     */
    const runGapAnalysisTechnicalEvaluation = useCallback(async (projectId, { impersonated = false, retryFailedOnly = false } = {}) => {
        if (!accounts || accounts.length === 0 || !projectId) {
            return { ok: false, status: 0, errorMessage: 'Not signed in.' };
        }
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                scopes: [process.env.REACT_APP_API_SCOPE],
                account: accounts[0],
            });
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/projects/${projectId}/gap-analysis/technical-evaluation`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${tokenResponse.accessToken}`,
                    ...(impersonated ? impersonationHeaders() : {}),
                },
                body: JSON.stringify(retryFailedOnly ? { retryFailedOnly: true } : {}),
            });
            const json = await res.json().catch(() => ({}));
            if (res.ok && json.success) return { ok: true, data: json.data };
            return technicalEvaluationFailure(res, json);
        } catch (error) {
            console.error('Failed to run technical AI evaluation:', error);
            return { ok: false, status: 0, errorMessage: toApiError(error).message };
        }
    }, [instance, accounts]);

    /**
     * Backlog Sheet-1 row 20 — "Review & Validate" — the explicit human-approval event
     * Commandment IV requires before `techScoresValidated` may become true. Takes no new AI
     * action; only the Advisor's (optional) score overrides.
     * @param {Record<string, {readinessScore?: number, expectedScore?: number}>} [techScoreOverrides]
     * @param {{ impersonated?: boolean }} [opts]
     * @returns {Promise<{ ok: true, data: object } | { ok: false, status: number, errorMessage: string }>}
     */
    const approveGapAnalysisTechnicalEvaluation = useCallback(async (projectId, techScoreOverrides = {}, { impersonated = false, evaluationId } = {}) => {
        if (!accounts || accounts.length === 0 || !projectId) {
            return { ok: false, status: 0, errorMessage: 'Not signed in.' };
        }
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                scopes: [process.env.REACT_APP_API_SCOPE],
                account: accounts[0],
            });
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/projects/${projectId}/gap-analysis/technical-evaluation/approve`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${tokenResponse.accessToken}`,
                    ...(impersonated ? impersonationHeaders() : {}),
                },
                body: JSON.stringify(evaluationId ? { techScoreOverrides, evaluationId } : { techScoreOverrides }),
            });
            const json = await res.json().catch(() => ({}));
            if (res.ok && json.success) return { ok: true, data: json.data };
            // 409 carries error.details.failedRowIds: rows that need a retry or a full override.
            return technicalEvaluationFailure(res, json);
        } catch (error) {
            console.error('Failed to approve technical AI evaluation:', error);
            return { ok: false, status: 0, errorMessage: toApiError(error).message };
        }
    }, [instance, accounts]);

    const fetchMeetings = useCallback(async () => {
        if (!accounts || accounts.length === 0) return [];
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                scopes: [process.env.REACT_APP_API_SCOPE],
                account: accounts[0],
            });
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/meetings`, {
                cache: 'no-store',
                headers: { 'Authorization': `Bearer ${tokenResponse.accessToken}` },
            });
            const json = await res.json();
            if (res.ok && json.success) return json.data.meetings || [];
            return [];
        } catch (error) {
            console.error('Failed to fetch meetings:', error);
            return [];
        }
    }, [instance, accounts]);

    /**
     * Gap Analysis per-question file upload (SPOC or delegate).
     * @returns {Promise<{ ok: true, data: object } | { ok: false, errorMessage: string }>}
     */
    const uploadGapQuestionFile = useCallback(
        async (projectId, file, { questionId, source }) => {
            if (!accounts || accounts.length === 0 || !projectId || !file || !questionId || !source) {
                return { ok: false, errorMessage: 'Not signed in or missing upload parameters.' };
            }
            try {
                const tokenResponse = await instance.acquireTokenSilent({
                    scopes: [process.env.REACT_APP_API_SCOPE],
                    account: accounts[0],
                });
                const formData = new FormData();
                formData.append('file', file);
                formData.append('questionId', questionId);
                formData.append('source', source);
                const res = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/api/v1/projects/${projectId}/gap-analysis/question-upload`,
                    {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
                        body: formData,
                    },
                );
                const json = await res.json().catch(() => ({}));
                if (res.ok && json.success && json.data) {
                    console.log('[uploadGapQuestionFile] success', {
                        projectId,
                        questionId,
                        source,
                        artifactId: json.data.artifactId,
                        fileName: json.data.fileName,
                    });
                    return { ok: true, data: json.data };
                }
                const msg = json.error?.message || `Upload failed (${res.status})`;
                console.error('[uploadGapQuestionFile] failed', {
                    projectId,
                    questionId,
                    source,
                    httpStatus: res.status,
                    message: msg,
                    error: json.error ?? json,
                });
                return { ok: false, errorMessage: msg };
            } catch (error) {
                console.error('Gap question upload error:', error);
                return { ok: false, errorMessage: error?.message || 'Upload failed' };
            }
        },
        [instance, accounts],
    );

    /**
     * Delete a Gap Analysis per-question evidence file.
     * `blobDeleteDenied` is additive (#153/#163): true only when the reference was removed
     * from the project but the underlying blob belongs to another tenant and was retained.
     * Absent/false on older responses and every normal delete — callers must treat that as
     * plain success (see lesson audit-log-unconditional-success-before-persisted-check).
     * @returns {Promise<{ ok: true, blobDeleteDenied: boolean } | { ok: false, errorMessage: string }>}
     */
    const deleteGapQuestionFile = useCallback(
        async (projectId, artifactId) => {
            if (!accounts || accounts.length === 0 || !projectId || !artifactId) {
                return { ok: false, errorMessage: 'Not signed in or missing parameters.' };
            }
            try {
                const tokenResponse = await instance.acquireTokenSilent({
                    scopes: [process.env.REACT_APP_API_SCOPE],
                    account: accounts[0],
                });
                const res = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/api/v1/projects/${projectId}/gap-analysis/question-file/${artifactId}`,
                    {
                        method: 'DELETE',
                        headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
                    },
                );
                const json = await res.json().catch(() => ({}));
                if (res.ok && json.success) {
                    return { ok: true, blobDeleteDenied: !!json.data?.blobDeleteDenied };
                }
                const msg = json.error?.message || `Delete failed (${res.status})`;
                return { ok: false, errorMessage: msg };
            } catch (error) {
                console.error('Gap question file delete error:', error);
                return { ok: false, errorMessage: error?.message || 'Delete failed' };
            }
        },
        [instance, accounts],
    );

    /**
     * Fetch a fresh read-only SAS URL for a blob and open it in a new tab.
     * Task #129's tenant-aware signer can legitimately return a null/missing url (cross-tenant
     * denial or an Azure credential-parse failure). Blob containers are private, so rawUrl is an
     * unsigned, inert 403/404 link — never fall back to opening it when the re-sign call fails or
     * throws (see lesson audit-log-unconditional-success-before-persisted-check). Returns `true`
     * if a signed URL was opened, `false` if signing failed — callers must surface the `false`
     * case to the user (e.g. message.error) rather than silently doing nothing.
     */
    const openBlobUrl = useCallback(
        async (rawUrl) => {
            if (!rawUrl) return false;
            if (!accounts || accounts.length === 0) {
                window.open(rawUrl, '_blank', 'noopener');
                return true;
            }
            try {
                const tokenResponse = await instance.acquireTokenSilent({
                    scopes: [process.env.REACT_APP_API_SCOPE],
                    account: accounts[0],
                });
                const res = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/api/v1/blob-sas?url=${encodeURIComponent(rawUrl)}`,
                    { headers: { Authorization: `Bearer ${tokenResponse.accessToken}` } },
                );
                const json = await res.json().catch(() => ({}));
                const sasUrl = (res.ok && json.success && json.data?.url) ? json.data.url : null;
                if (!sasUrl) return false;
                window.open(sasUrl, '_blank', 'noopener');
                return true;
            } catch {
                return false;
            }
        },
        [instance, accounts],
    );

    const value = {
        // User
        user,
        setUser,
        signIn,
        signOut,

        // Navigation
        currentView,
        setCurrentView,
        sidebarOpen,
        setSidebarOpen,
        toggleSidebar: () => setSidebarOpen((v) => !v),

        // Projects
        projects,
        projectsLoading,
        activeProject,
        selectProject,
        workspaceStepOverrideRef,
        profilingSubTabOverrideRef,
        riskAnalysisSubTabOverrideRef,
        evidencingSubTabOverrideRef,
        exitProject,
        addProject,
        fetchProjects,
        fetchProjectMembers,
        toggleProjectStatus,
        deleteProject,
        updateProjectMembers,
        updateProjectProfile,
        setActiveWorkspaceStep,
        updateProfilingStatus,
        fetchProjectProfile,
        saveProjectProfile,
        saveScopingProfile,
        updateProfilingStatusAPI,
        updateScopingStatusAPI,
        updateRegulatoryRoleAPI,
        confirmRegulatoryRoleAPI,
        fetchMilestones,
        saveMilestones,
        generateMilestones,
        fetchIso27001Controls,

        // Notifications
        notifications,
        addNotification,
        fetchNotifications,
        markNotificationRead,
        markAllNotificationsRead,

        // Meetings
        createMeeting,
        updateMeeting,
        updateMeetingAttendees,
        generateAiScores,
        runGapAnalysisTechnicalEvaluation,
        approveGapAnalysisTechnicalEvaluation,
        fetchMeetings,
        uploadGapQuestionFile,
        deleteGapQuestionFile,
        openBlobUrl,

        taxonomyPendingCount,
        fetchTaxonomyPendingCount,
    };

    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

/**
 * Hook to access the AppContext.
 */
export function useApp() {
    const ctx = useContext(AppContext);
    if (!ctx) throw new Error('useApp must be used within <AppProvider>');
    return ctx;
}
